/* global __APP_VERSION__ */

import { useEffect } from "react";
import { useHeaderContext } from "../../context/headerContext";
import ContentCard from "../../components/ContentCard";
import { useDrawerContext } from "../../context/drawerContext";
import { useThemeContext } from "../../context/themeContext";
import { useUserContext } from "../../context/userContext";
import Button from "../../components/Button";
import { Link } from "react-router-dom";
import CloseIcon from "/public/images/icons/close.svg?react";
import EmailIcon from "/public/images/icons/email.svg?react";
import PhoneIcon from "/public/images/icons/phone.svg?react";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { useLiteDrawer } from "../../hooks/useLiteDrawer";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Settings() {
  const { setTitle, setBackButton, setSearchFunc } = useHeaderContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const { theme } = useThemeContext();
  const { logout, user, updateTokens, selectedVenue } = useUserContext();
  const { showLiteDrawer } = useLiteDrawer();

  useEffect(() => {
    setTitle("Settings");
    setBackButton(true);
    setSearchFunc(null);
  }, []);

  const logOutStaffMember = async () => {
    if (localStorage.getItem("logged-in") && localStorage.getItem("firebaseToken")) {
      const deviceId = localStorage.getItem("firebaseToken");
  
      try {
        await Sentry.startSpan({ name: "Log Out Staff", op: "http.request" }, async (span) => {
          const url = `${import.meta.env.VITE_API_URL}Staff/RemoveLoggedInStaffMember`;
          span.setAttribute("http.url", url);
          span.setAttribute("staffId", user?.id);
          span.setAttribute("venueId", selectedVenue?.OrgID);
  
          const response = await fetchWithRefresh(
            url,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
              body: JSON.stringify({
                venueId: selectedVenue?.OrgID.toString(),
                staffId: user?.id,
                deviceId: deviceId,
              }),
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
  
          if (response.ok) {
            LogErrors(user, logout, updateTokens, "Record successfully deleted");
          } else {
            LogErrors(user, logout, updateTokens, "Record unsuccessfully deleted");
          }
        });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { venueId: selectedVenue?.OrgID, staffId: user?.id, deviceId },
        });
        LogErrors(user, logout, updateTokens, "Error logging out staff member: " + error);
      }
    }
  };

  const contactDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Contact
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">Need some assistance?</h3>
          <p className=" text-sportsDarkGrey">
            We&apos;re happy to assist with any query. Our offices are open
            Monday - Friday between 09:00 - 16:30.
          </p>
          <Link className="flex items-center gap-2" to="tel:0800 999 3669">
            <PhoneIcon />{" "}
            <Button
              status="nobg"
              variant="tertiary"
              onClick={() => setOpen(false)}
            >
              0800 999 3669
            </Button>
          </Link>
          <Link
            className="flex items-center gap-2"
            to="mailto:hello@sportsguardian.com"
          >
            <EmailIcon />{" "}
            <Button
              status="nobg"
              variant="tertiary"
              onClick={() => setOpen(false)}
            >
              hello@sportsguardian.com
            </Button>{" "}
          </Link>
        </div>
      </div>
    );
  };

  const logoutDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Log Out
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">Log out, are you sure?</h3>
          <Button
            onClick={() => {
              logOutStaffMember(), logout(), setOpen(false);
            }}
          >
            Log Out
          </Button>
          <Button
            status="nobg"
            variant="tertiary"
            onClick={() => setOpen(false)}
          >
            Stay Signed In
          </Button>
        </div>
      </div>
    );
  };

  const participantTiles = [
    { text: "Account", link: "/account", icon: "/images/icons/participantAvatarIcon.svg" },
    {
      text: "FAQs",
      link: "https://www.sportsguardian.com/fa-qs",
      target: "_blank",
      icon: "/images/icons/accountFaqIcon.svg",
    },
    {
      text: "Order New Band",
      link: "/shop/my-participants",
      icon: "/images/icons/accountReplaceBandIcon.svg",
      func: user?.subscription?.Name === "Lite" ? showLiteDrawer : undefined,
    },
    {
      text: "Contact",
      link: "/contact",
      icon: "/images/icons/contactIcon.svg",
      func: contactDrawer,
    },
    {
      text: "User Guide",
      link: "https://sportsguardian.b-cdn.net/SG_Member_Guide_Feb25.pdf",
      icon: "/images/icons/userGuideIcon.svg",
      target: "_blank",
    },
    {
      text: "Log Out",
      link: "/logout",
      icon: "/images/icons/logOutIcon.svg",
      func: logoutDrawer,
    },
  ];

  const staffTiles = [
    { text: "Account", link: "/account", icon: "/images/icons/staffAvatarIcon.svg" },
    {
      text: "Venue Data",
      link: `/weather/${selectedVenue?.OrgID}`,
      icon: "/images/icons/staffWeatherIcon.svg",
    },
    {
      text: "Venue Shop",
      link: "/venue-shop",
      icon: "/images/icons/venueShopQRIcon.svg",
    },
    {
      text: "Staff Guide",
      link: "https://sportsguardian.b-cdn.net/SG_Venue_Staff_Guide_Feb25.pdf",
      icon: "/images/icons/staffGuideIcon.svg",
      target: "_blank",
    },
    {
      text: "Contact",
      link: "/contact",
      icon: "/images/icons/staffContactIcon.svg",
      func: contactDrawer,
    },
    {
      text: "Log Out",
      link: "/logout",
      icon: "/images/icons/staffLogOutIcon.svg",
      func: logoutDrawer,
    },
  ];

  const tiles = theme === "staff" ? staffTiles : participantTiles;

  console.log(__APP_VERSION__);

  return (
    <>
      <div className="flex flex-wrap -mx-2">
        {tiles.map((tile, index) => (
          <ContentCard
            key={index}
            title={tile?.text}
            icon={tile?.icon}
            href={tile?.link}
            target={tile?.target}
            onClick={tile?.func}
            type="account"
          />
        ))}
      </div>
      <div className="flex flex-col items-center underline text-[#101820] font-semibold mt-[32px]">
        <a href="https://www.sportsguardian.com/terms" target="_blank">
          Terms of Service
        </a>
      </div>
      <span className="text-sportsDarkGrey text-xs mt-2 flex justify-center w-full">
        Version {__APP_VERSION__}
      </span>
    </>
  );
}
